import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, shade } from './draw-utils';
import { darkCore, dangerHalo, hostile, rgba, spikes } from './bullets-danger';
import type { ProceduralDef } from '../types';

/*
 * Per-boss enemy bullet identities (docs/02, Batch 2.19) in the danger
 * language (docs/04, wave 4): every one keeps a hot red/orange rim, a dark
 * core and a spiked or finned silhouette — away from the player cyan/white
 * family, the weapon-category colours AND the loot palette (gold, emerald,
 * blue), so no hostile shot reads as a pickup.
 */

/** Void eclipse — Null Crown / Singularity: black disc, hot corona flares. */
export const bulletEnemyVoid: ProceduralDef = hostile('bullet/enemy/void', 48, 48, (ctx, w, _h, k) => {
  const c = w / 2;
  const r = w / 2 - 7;
  const corona = COLORS.enemyVoidCorona;
  dangerHalo(ctx, c, r * 0.6, w / 2, corona, k);
  spikes(ctx, c, c, r * 0.9, r * 1.3, 8, Math.PI / 8, 0.2, corona);
  circle(ctx, c, c, r);
  fillOutlined(ctx, corona, 3.5);
  // The eclipse itself dominates the read
  circle(ctx, c, c, r * 0.74);
  ctx.fillStyle = css(COLORS.enemyVoidCore);
  ctx.fill();
  ctx.strokeStyle = css(shade(corona, 1.2));
  ctx.lineWidth = 1.5;
  ctx.stroke();
});

/** Rock chunk — Terra's shrapnel: jagged basalt, a hot magma rim + crack. */
export const bulletEnemyRock: ProceduralDef = hostile('bullet/enemy/rock', 50, 50, (ctx, w, _h, k) => {
  const c = w / 2;
  const r = w / 2 - 8;
  dangerHalo(ctx, c, r * 0.6, w / 2, COLORS.terraCrack, k * 0.8);
  // Jagged boulder: five corners plus two sharp chips
  const pts: ReadonlyArray<readonly [number, number]> = [
    [0, -1], [0.45, -0.55], [0.95, -0.25], [0.6, 0.9], [0.05, 0.62],
    [-0.7, 0.8], [-1.05, 0.05], [-0.55, -0.45],
  ];
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(c + x * r, c + y * r) : ctx.lineTo(c + x * r, c + y * r)));
  ctx.closePath();
  // Dark basalt (a dark body, far from the gold loot) with a magma rim
  fillOutlined(ctx, shade(COLORS.terraStone, 0.5), 4);
  ctx.strokeStyle = css(COLORS.terraCrack);
  ctx.lineWidth = 2.2;
  ctx.stroke();
  // Glowing magma crack
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(c - r * 0.3, c - r * 0.5);
  ctx.lineTo(c + r * 0.1, c + r * 0.05);
  ctx.lineTo(c - r * 0.1, c + r * 0.6);
  ctx.stroke();
});

/** Wind crescent — Gale: a hot coral slash with barbed tips, dark inner edge. */
export const bulletEnemyWind: ProceduralDef = hostile('bullet/enemy/wind', 50, 50, (ctx, w, _h, k) => {
  const c = w / 2;
  const r = w / 2 - 7;
  dangerHalo(ctx, c, r * 0.4, w / 2, COLORS.enemyWind, k);
  ctx.beginPath();
  ctx.arc(c, c, r, Math.PI * 0.15, Math.PI * 1.05);
  ctx.lineTo(c + Math.cos(Math.PI * 1.05) * r * 1.2, c + Math.sin(Math.PI * 1.05) * r * 1.2);
  ctx.arc(c + r * 0.4, c, r * 0.85, Math.PI * 1.05, Math.PI * 0.15, true);
  ctx.lineTo(c + Math.cos(Math.PI * 0.15) * r * 1.2, c + Math.sin(Math.PI * 0.15) * r * 1.2);
  ctx.closePath();
  fillOutlined(ctx, COLORS.enemyWind, 3.5);
  // Dark inner edge — the hollow of the crescent
  ctx.beginPath();
  ctx.arc(c + r * 0.4, c, r * 0.85, Math.PI * 0.95, Math.PI * 0.25, true);
  ctx.strokeStyle = rgba(COLORS.bulletCoreDark, 0.75);
  ctx.lineWidth = 3;
  ctx.stroke();
  circle(ctx, c - r * 0.45, c, r * 0.18);
  ctx.fillStyle = css(COLORS.enemyWindEdge);
  ctx.fill();
});

/**
 * Enemy bolt — an elongated energy DART that points along its travel
 * (rotates: true): red-orange lance, swept fins, a dark slit core. For tech
 * bosses (Sentinel, Carrier, Admiral, Bastion, Warden, Stalker).
 */
export const bulletEnemyBolt: ProceduralDef = hostile('bullet/enemy/bolt', 64, 26, (ctx, w, h, k) => {
  const cy = h / 2;
  const grad = ctx.createLinearGradient(0, 0, w, 0);
  grad.addColorStop(0, rgba(COLORS.enemyBolt, 0));
  grad.addColorStop(1, rgba(COLORS.enemyBolt, Math.min(0.85, 0.45 * k)));
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.moveTo(4, cy);
  ctx.lineTo(w - 18, cy - 7);
  ctx.lineTo(w - 18, cy + 7);
  ctx.closePath();
  ctx.fill();
  // Swept tail fins
  ctx.beginPath();
  ctx.moveTo(w - 34, cy - 10);
  ctx.lineTo(w - 24, cy - 3);
  ctx.lineTo(w - 24, cy + 3);
  ctx.lineTo(w - 34, cy + 10);
  ctx.lineTo(w - 29, cy);
  ctx.closePath();
  fillOutlined(ctx, shade(COLORS.enemyBolt, 0.8), 2.5);
  // Lance head (points +x)
  ctx.beginPath();
  ctx.moveTo(w - 3, cy);
  ctx.lineTo(w - 22, cy - 9);
  ctx.lineTo(w - 30, cy);
  ctx.lineTo(w - 22, cy + 9);
  ctx.closePath();
  fillOutlined(ctx, COLORS.enemyBolt, 3);
  ctx.beginPath();
  ctx.moveTo(w - 9, cy);
  ctx.lineTo(w - 21, cy - 3.5);
  ctx.lineTo(w - 24, cy);
  ctx.lineTo(w - 21, cy + 3.5);
  ctx.closePath();
  ctx.fillStyle = css(COLORS.bulletCoreDark);
  ctx.fill();
  circle(ctx, w - 6, cy, 2);
  ctx.fillStyle = css(COLORS.enemyBoltTip);
  ctx.fill();
});

/**
 * Enemy shard — geometric bosses (Prism, Quartet, Mirror, Carrier,
 * Chronos): a crimson three-blade SPLINTER around a dark hollow. It used to
 * be a blue faceted diamond — the exact silhouette of a gem pickup.
 */
export const bulletEnemyShard: ProceduralDef = hostile('bullet/enemy/shard', 46, 46, (ctx, w, _h, k) => {
  const c = w / 2;
  const r = w / 2 - 5;
  drawSplinter(ctx, c, r, k);
});

/**
 * Prism's lens bolt — the splinter plus a hot ember on each blade tip, one
 * per shard it refracts into. A plain pulse with NO fuse ring: the bomb read
 * (fuse + danger ring) is reserved for real explosives (docs/04).
 */
export const bulletEnemyLensBolt: ProceduralDef = hostile('bullet/enemy/lens-bolt', 50, 50, (ctx, w, _h, k) => {
  const c = w / 2;
  const r = w / 2 - 5;
  drawSplinter(ctx, c, r, k);
  ctx.fillStyle = css(COLORS.enemyEmber);
  for (let i = 0; i < 3; i++) {
    const a = -Math.PI / 2 + (Math.PI * 2 * i) / 3;
    circle(ctx, c + Math.cos(a) * r * 0.86, c + Math.sin(a) * r * 0.86, r * (0.08 + 0.04 * k));
    ctx.fill();
  }
});

/** Crimson splinter body (shared with the lens bolt). */
export function drawSplinter(ctx: CanvasRenderingContext2D, c: number, r: number, k: number): void {
  dangerHalo(ctx, c, r * 0.3, r * 1.12, COLORS.enemyShard, k);
  ctx.beginPath();
  for (let i = 0; i < 3; i++) {
    const a = -Math.PI / 2 + (Math.PI * 2 * i) / 3;
    const at = (ang: number, rr: number): [number, number] =>
      [c + Math.cos(ang) * rr, c + Math.sin(ang) * rr];
    const [x0, y0] = at(a - 1.05, r * 0.36);
    if (i === 0) ctx.moveTo(x0, y0);
    else ctx.lineTo(x0, y0);
    ctx.lineTo(...at(a - 0.2, r * 0.7));
    ctx.lineTo(...at(a, r));
    ctx.lineTo(...at(a + 0.16, r * 0.62));
    ctx.lineTo(...at(a + 0.34, r * 0.66));
  }
  ctx.closePath();
  fillOutlined(ctx, COLORS.enemyShard, 3);
  // Dark hollow triangle + a hot edge on one blade (no white glints)
  ctx.beginPath();
  for (let i = 0; i < 3; i++) {
    const a = Math.PI / 2 + (Math.PI * 2 * i) / 3;
    const x = c + Math.cos(a) * r * 0.3;
    const y = c + Math.sin(a) * r * 0.3;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = css(COLORS.bulletCoreDark);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(c, c - r * 0.45);
  ctx.lineTo(c, c - r * 0.85);
  ctx.strokeStyle = css(COLORS.enemyEmber);
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.stroke();
}

/**
 * Enemy spore — hive/worm bosses (Hive, Splitter, Broodworm): a lumpy
 * crimson-rose pod bristling with spines, maroon heart. It used to be olive,
 * one brightness step from the player's laser lime.
 */
export const bulletEnemySpore: ProceduralDef = hostile('bullet/enemy/spore', 48, 48, (ctx, w, _h, k) => {
  const c = w / 2;
  const r = w / 2 - 9;
  drawSporePod(ctx, c, r, k);
});

/** Spiny spore pod body (shared with the fused egg). */
export function drawSporePod(ctx: CanvasRenderingContext2D, c: number, r: number, k: number): void {
  dangerHalo(ctx, c, r * 0.6, r * 1.6, COLORS.enemySpore, k);
  spikes(ctx, c, c, r * 0.8, r * 1.35, 9, 0.2, 0.15, shade(COLORS.enemySpore, 0.85));
  ctx.beginPath();
  for (let i = 0; i <= 12; i++) {
    const a = (Math.PI * 2 * i) / 12;
    const lump = r + Math.sin(a * 3) * r * 0.16;
    const x = c + Math.cos(a) * lump;
    const y = c + Math.sin(a) * lump;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  fillOutlined(ctx, COLORS.enemySpore, 3.5);
  circle(ctx, c, c, r * 0.5);
  ctx.fillStyle = css(COLORS.enemySporeCore);
  ctx.fill();
  circle(ctx, c + r * 0.12, c - r * 0.15, r * 0.14);
  ctx.fillStyle = rgba(COLORS.enemyEmber, 0.85);
  ctx.fill();
}

/** Enemy star — Magnet / Chronos: a coral 4-point throwing star, dark core. */
export const bulletEnemyStar: ProceduralDef = hostile('bullet/enemy/star', 50, 50, (ctx, w, _h, k) => {
  const c = w / 2;
  const r = w / 2 - 5;
  dangerHalo(ctx, c, r * 0.3, w / 2, COLORS.enemyStar, k);
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (Math.PI * i) / 4 - Math.PI / 2;
    const rr = i % 2 === 0 ? r : r * 0.36;
    const x = c + Math.cos(a) * rr;
    const y = c + Math.sin(a) * rr;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  fillOutlined(ctx, COLORS.enemyStar, 3);
  darkCore(ctx, c, c, r * 0.28, COLORS.enemyEmber);
});
